/**
 * Finanzas → Caja Chica (`/finanzas/caja-chica`).
 *
 * Portado de `Caja_chica::index` + `views/caja_chica/index.php` y de su bundle
 * `src/caja_chica/index.js`, que pedía los datos a la API interna
 * `API/Caja_chica` (modelo `Caja_chica_model`).
 *
 * La pantalla es el **efectivo administrativo** de la estación: arriba el saldo
 * disponible y los contadores del periodo, luego los botones de las cinco
 * operaciones (saldo inicial, reajuste, ingreso, egreso y depósito a banco) y
 * debajo el historial con sus totales.
 *
 * Reglas heredadas:
 * - El rango por defecto es el **mes en curso** (del día 1 a hoy), como el
 *   original.
 * - El saldo inicial sólo se ofrece con el libro vacío; el resto de botones
 *   dependen de los permisos `caja_chica.*` de la sesión.
 * - El pie del listado suma ingresos y egresos del **filtro completo** (no de la
 *   página) y deja fuera los anulados y los movimientos del sistema.
 * - Revertir no borra: crea el movimiento inverso, marca el original y, si era
 *   un depósito a banco, anula también el movimiento bancario.
 */

import {
  ArrowDownLeft,
  ArrowUpRight,
  Ban,
  FileSpreadsheet,
  FileText,
  Landmark,
  Paperclip,
  PiggyBank,
  PlusCircle,
  RefreshCw,
  Repeat,
  Search,
  SlidersHorizontal,
} from 'lucide-react';
import { useMemo, useRef, useState } from 'react';

import { Alert } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { CampoFecha } from '@/components/ui/calendario';
import { ConfirmDialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { abrirPdfAutenticado } from '@/lib/descargas';
import { nuevaClaveDeEnvio } from '@/lib/clave-de-envio';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { ApiError } from '@/lib/http/api-error';
import { cn, formatCurrency, formatDateValue, toIsoDate } from '@/lib/utils';

import { cajaChicaApi, useAjustarCaja, useAperturarCaja, useDepositarCaja, useHistorialCaja, useRegistrarEgresoCaja, useRegistrarIngresoCaja, useResumenCaja, useRevertirMovimientoCaja } from '../caja-chica.api';
import {
  DialogoAperturaCaja,
  DialogoDepositoCaja,
  DialogoMovimientoCaja,
  DialogoReajusteCaja,
  type DatosMovimientoCaja,
} from '../components/caja-chica-dialogos';
import type { AdjuntoCaja, FiltrosCaja, MovimientoCaja } from '../caja-chica.types';

const CONTROL =
  'h-10 w-full rounded-control border border-ink-200 bg-white px-3 text-sm text-ink-900 ' +
  'transition-colors focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:outline-none';

const TAMANOS = [10, 25, 50, 100];

/** Primer día del mes en curso. */
function inicioDeMes(): string {
  const hoy = new Date();

  return toIsoDate(new Date(hoy.getFullYear(), hoy.getMonth(), 1));
}

/** Tipos ofrecidos en el filtro (el original ofrecía todos, ingresos y egresos). */
const TIPOS = [
  { valor: '', etiqueta: 'Todos los tipos' },
  { valor: 'ingreso', etiqueta: 'Ingresos' },
  { valor: 'egreso', etiqueta: 'Egresos' },
  { valor: 'apertura', etiqueta: 'Aperturas' },
  { valor: 'ajuste', etiqueta: 'Ajustes' },
];

type DialogoAbierto = 'ingreso' | 'egreso' | 'apertura' | 'reajuste' | 'deposito' | null;

export function CajaChicaPage() {
  useDocumentTitle('Finanzas · Caja Chica');

  const [desde, setDesde] = useState(inicioDeMes);
  const [hasta, setHasta] = useState(() => toIsoDate(new Date()));
  const [tipo, setTipo] = useState('');
  const [categoria, setCategoria] = useState('');
  const [busqueda, setBusqueda] = useState('');
  const [pagina, setPagina] = useState(1);
  const [limite, setLimite] = useState(10);
  const [dialogo, setDialogo] = useState<DialogoAbierto>(null);
  const [porRevertir, setPorRevertir] = useState<MovimientoCaja | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const filtros = useMemo<FiltrosCaja>(
    () => ({
      desde,
      hasta,
      tipo,
      categoria,
      buscar: busqueda.trim(),
      page: pagina,
      perPage: limite,
    }),
    [desde, hasta, tipo, categoria, busqueda, pagina, limite],
  );

  const resumen = useResumenCaja(desde, hasta);
  const historial = useHistorialCaja(filtros);

  const ingresar = useRegistrarIngresoCaja();
  const egresar = useRegistrarEgresoCaja();
  const aperturar = useAperturarCaja();
  const ajustar = useAjustarCaja();
  const depositar = useDepositarCaja();
  const revertir = useRevertirMovimientoCaja();

  // Clave de un solo envío por operación: si el envío se repite (timeout de red), el API
  // devuelve el movimiento ya registrado en lugar de duplicarlo. Se renueva al confirmar.
  const clavesDeCaja = useRef<Record<string, string>>({});
  const claveDe = (operacion: string) => (clavesDeCaja.current[operacion] ??= nuevaClaveDeEnvio());

  const saldo = resumen.data?.saldo_actual ?? 0;
  const permisos = resumen.data?.permisos ?? historial.data?.permisos;
  const totales = historial.data?.totales ?? resumen.data?.totales_periodo;
  const movimientos = historial.data?.movimientos ?? [];
  const total = historial.data?.total ?? 0;
  const paginas = historial.data?.paginas ?? 1;
  const hoy = resumen.data?.hoy ?? toIsoDate(new Date());
  const accionando =
    ingresar.isPending ||
    egresar.isPending ||
    aperturar.isPending ||
    ajustar.isPending ||
    depositar.isPending;

  /** Cambiar cualquier filtro vuelve a la primera página. */
  const filtrar = (cambio: () => void) => {
    setPagina(1);
    cambio();
  };

  /** Deja la pantalla lista tras una operación: cierra el diálogo y avisa. */
  const terminado = (mensaje: string) => () => {
    clavesDeCaja.current = {};
    setDialogo(null);
    setError(null);
    setAviso(mensaje);
    setTimeout(() => setAviso(null), 6000);
  };

  const fallo = (mensaje: string) => (causa: unknown) => {
    setError(causa instanceof Error ? causa.message : mensaje);
  };

  const guardarMovimiento = (datos: DatosMovimientoCaja) => {
    setError(null);

    if (dialogo === 'ingreso') {
      ingresar.mutate(
        { ...datos, clave_idempotencia: claveDe('ingreso') },
        {
          onSuccess: terminado('Ingreso registrado correctamente.'),
          onError: fallo('No se pudo registrar el ingreso.'),
        },
      );

      return;
    }

    egresar.mutate(
      { ...datos, clave_idempotencia: claveDe('egreso') },
      {
        onSuccess: terminado('Egreso registrado correctamente.'),
        onError: fallo('No se pudo registrar el egreso.'),
      },
    );
  };

  const exportar = async (formato: 'csv' | 'pdf') => {
    setError(null);

    try {
      await abrirPdfAutenticado(cajaChicaApi.urlExportar(filtros, formato), {
        nombre: `caja-chica-${desde}_${hasta}.${formato}`,
      });
    } catch (causa) {
      setError(causa instanceof Error ? causa.message : 'No se pudo generar la exportación.');
    }
  };

  /**
   * Abre el comprobante de un movimiento. Los archivos los sirve la API y piden
   * la sesión, así que se traen con el token (no vale un enlace normal).
   */
  const abrirAdjunto = async (adjunto: AdjuntoCaja) => {
    setError(null);

    try {
      await abrirPdfAutenticado(adjunto.url, { nombre: adjunto.archivo });
    } catch (causa) {
      setError(causa instanceof Error ? causa.message : 'No se pudo abrir el comprobante.');
    }
  };

  const confirmarReversion = () => {
    if (!porRevertir) return;

    setError(null);

    revertir.mutate(porRevertir.id, {
      onSuccess: (datos) => {
        setAviso(datos.message);
        setPorRevertir(null);
        setTimeout(() => setAviso(null), 8000);
      },
      onError: (causa) => {
        setError(causa instanceof Error ? causa.message : 'No se pudo revertir el movimiento.');
        setPorRevertir(null);
      },
    });
  };

  const errorConsulta =
    historial.isError && historial.error instanceof ApiError ? historial.error.message : null;

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-ink-900">Caja Chica</h1>
          <p className="mt-1 text-sm text-ink-500">
            El efectivo administrativo de la estación: su saldo disponible, los adelantos y
            devoluciones que entran, los gastos menores que salen y los depósitos al banco.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {permisos?.exportar ? (
            <>
              <Button
                variant="outline"
                size="sm"
                leftIcon={<FileSpreadsheet className="size-4" aria-hidden="true" />}
                onClick={() => void exportar('csv')}
              >
                Excel
              </Button>
              <Button
                variant="outline"
                size="sm"
                leftIcon={<FileText className="size-4" aria-hidden="true" />}
                onClick={() => void exportar('pdf')}
              >
                PDF
              </Button>
            </>
          ) : null}

          <Button
            variant="outline"
            size="sm"
            leftIcon={
              <RefreshCw
                className={cn('size-4', historial.isFetching && 'animate-spin')}
                aria-hidden="true"
              />
            }
            onClick={() => {
              void resumen.refetch();
              void historial.refetch();
            }}
          >
            Actualizar
          </Button>
        </div>
      </header>

      {aviso ? (
        <Alert variant="success" title="Listo">
          {aviso}
        </Alert>
      ) : null}

      {error ? (
        <Alert variant="error" title="No se pudo completar la acción">
          {error}
        </Alert>
      ) : null}

      {/* Contadores: el saldo disponible manda, luego el movimiento del rango. */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <Card className="border-brand-200 bg-brand-50/60 sm:col-span-2 xl:col-span-1">
          <CardContent className="flex items-center gap-3 p-4">
            <span
              aria-hidden="true"
              className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-brand-100 text-brand-700"
            >
              <PiggyBank className="size-5" />
            </span>
            <div className="min-w-0">
              <p className="text-[11px] font-semibold tracking-wide text-ink-500 uppercase">
                Saldo disponible
              </p>
              <p className="text-base font-bold break-words text-brand-700 tabular-nums sm:text-lg">
                {formatCurrency(saldo)}
              </p>
              <p className="text-xs text-ink-500">Todo el libro de caja chica</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="flex items-center gap-3 p-4">
            <span
              aria-hidden="true"
              className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600"
            >
              <ArrowUpRight className="size-5" />
            </span>
            <div className="min-w-0">
              <p className="text-[11px] font-semibold tracking-wide text-ink-500 uppercase">
                Ingresos del rango
              </p>
              <p className="text-base font-bold break-words text-emerald-700 tabular-nums sm:text-lg">
                {formatCurrency(totales?.ingresos ?? 0)}
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="flex items-center gap-3 p-4">
            <span
              aria-hidden="true"
              className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-rose-50 text-rose-600"
            >
              <ArrowDownLeft className="size-5" />
            </span>
            <div className="min-w-0">
              <p className="text-[11px] font-semibold tracking-wide text-ink-500 uppercase">
                Egresos del rango
              </p>
              <p className="text-base font-bold break-words text-rose-700 tabular-nums sm:text-lg">
                {formatCurrency(totales?.egresos ?? 0)}
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="flex items-center gap-3 p-4">
            <span
              aria-hidden="true"
              className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-amber-50 text-amber-600"
            >
              <Repeat className="size-5" />
            </span>
            <div className="min-w-0">
              <p className="text-[11px] font-semibold tracking-wide text-ink-500 uppercase">
                Movimientos del rango
              </p>
              <p className="text-base font-bold break-words text-amber-700 tabular-nums sm:text-lg">
                {(totales?.movimientos ?? 0).toLocaleString('es-PE')}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Operaciones */}
      <Card>
        <CardContent className="flex flex-wrap items-center gap-2 p-4">
          {permisos?.aperturar && resumen.data?.puede_aperturar ? (
            <Button
              variant="outline"
              size="sm"
              leftIcon={<PlusCircle className="size-4" aria-hidden="true" />}
              onClick={() => {
                setError(null);
                setDialogo('apertura');
              }}
            >
              Registrar saldo inicial
            </Button>
          ) : null}

          {permisos?.reajustar ? (
            <Button
              variant="outline"
              size="sm"
              leftIcon={<SlidersHorizontal className="size-4" aria-hidden="true" />}
              onClick={() => {
                setError(null);
                setDialogo('reajuste');
              }}
            >
              Reajustar saldo
            </Button>
          ) : null}

          {permisos?.registrar_ingreso ? (
            <Button
              size="sm"
              leftIcon={<ArrowUpRight className="size-4" aria-hidden="true" />}
              onClick={() => {
                setError(null);
                setDialogo('ingreso');
              }}
            >
              Registrar ingreso
            </Button>
          ) : null}

          {permisos?.registrar_egreso ? (
            <>
              <Button
                variant="danger"
                size="sm"
                leftIcon={<ArrowDownLeft className="size-4" aria-hidden="true" />}
                onClick={() => {
                  setError(null);
                  setDialogo('egreso');
                }}
              >
                Registrar egreso
              </Button>
              <Button
                variant="secondary"
                size="sm"
                leftIcon={<Landmark className="size-4" aria-hidden="true" />}
                onClick={() => {
                  setError(null);
                  setDialogo('deposito');
                }}
              >
                Depositar a banco
              </Button>
            </>
          ) : null}

          <span className="ml-auto text-xs text-ink-500">
            {resumen.data?.puede_aperturar
              ? 'El libro está vacío: registra el saldo inicial para empezar.'
              : `Saldo disponible ${formatCurrency(saldo)}`}
          </span>
        </CardContent>
      </Card>

      {/* Filtros */}
      <Card>
        <CardContent className="p-4">
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <label className="flex flex-col gap-1 text-xs font-medium text-ink-500">
              Desde
              <CampoFecha
                tipo="date"
                value={desde}
                max={hasta}
                onChange={(evento) => filtrar(() => setDesde(evento.target.value))}
                aria-label="Fecha de inicio"
              />
            </label>

            <label className="flex flex-col gap-1 text-xs font-medium text-ink-500">
              Hasta
              <CampoFecha
                tipo="date"
                value={hasta}
                min={desde}
                onChange={(evento) => filtrar(() => setHasta(evento.target.value))}
                aria-label="Fecha de fin"
              />
            </label>

            <label className="flex flex-col gap-1 text-xs font-medium text-ink-500">
              Tipo
              <Select
                value={tipo}
                onChange={(evento) => filtrar(() => setTipo(evento.target.value))}
                className={CONTROL}
                aria-label="Tipo de movimiento"
              >
                {TIPOS.map((opcion) => (
                  <option key={opcion.valor} value={opcion.valor}>
                    {opcion.etiqueta}
                  </option>
                ))}
              </Select>
            </label>

            <label className="flex flex-col gap-1 text-xs font-medium text-ink-500">
              Categoría
              <Select
                value={categoria}
                onChange={(evento) => filtrar(() => setCategoria(evento.target.value))}
                className={CONTROL}
                aria-label="Categoría"
              >
                <option value="">Todas las categorías</option>
                {(resumen.data?.categorias ?? []).map((opcion) => (
                  <option key={opcion.valor} value={opcion.valor}>
                    {opcion.etiqueta}
                  </option>
                ))}
              </Select>
            </label>
          </div>

          <div className="mt-3 flex flex-wrap items-end gap-3">
            <label className="flex min-w-[16rem] flex-1 flex-col gap-1 text-xs font-medium text-ink-500">
              Buscar
              <span className="relative block">
                <Search
                  className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-ink-400"
                  aria-hidden="true"
                />
                <Input
                  value={busqueda}
                  onChange={(evento) => filtrar(() => setBusqueda(evento.target.value))}
                  placeholder="Concepto, referencia, categoría, usuario…"
                  className="pl-9"
                  aria-label="Buscar movimiento"
                />
              </span>
            </label>

            <label className="flex flex-col gap-1 text-xs font-medium text-ink-500">
              Filas
              <Select
                value={String(limite)}
                onChange={(evento) => filtrar(() => setLimite(Number(evento.target.value)))}
                className={cn(CONTROL, 'w-24')}
                aria-label="Filas por página"
              >
                {TAMANOS.map((tamano) => (
                  <option key={tamano} value={String(tamano)}>
                    {tamano}
                  </option>
                ))}
              </Select>
            </label>

            <span className="ml-auto text-xs text-ink-500">
              {total.toLocaleString('es-PE')} movimiento{total === 1 ? '' : 's'}
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Historial */}
      <Card>
        <CardContent className="space-y-3 p-4">
          {errorConsulta ? (
            <Alert variant="error" title="No se pudo leer el historial">
              {errorConsulta}
            </Alert>
          ) : null}

          <div className="overflow-auto rounded-card border border-ink-100">
            <table className="w-full border-collapse text-[13px]">
              <thead className="sticky top-0 z-10 bg-white shadow-[0_1px_0_0_var(--color-ink-100)]">
                <tr className="text-left text-xs font-semibold tracking-wide text-ink-500 uppercase">
                  <th className="px-2 py-2 whitespace-nowrap">#</th>
                  <th className="px-2 py-2 whitespace-nowrap">Fecha</th>
                  <th className="px-2 py-2 whitespace-nowrap">Tipo</th>
                  <th className="px-2 py-2 whitespace-nowrap">Categoría</th>
                  <th className="px-2 py-2">Concepto</th>
                  <th className="px-2 py-2">Referencia</th>
                  <th className="px-2 py-2 text-right whitespace-nowrap">Ingreso</th>
                  <th className="px-2 py-2 text-right whitespace-nowrap">Egreso</th>
                  <th className="px-2 py-2 text-right whitespace-nowrap">Saldo</th>
                  <th className="px-2 py-2 whitespace-nowrap">Registrado</th>
                  <th className="px-2 py-2 text-center whitespace-nowrap">Adjunto</th>
                  <th className="px-2 py-2 text-right whitespace-nowrap">Acciones</th>
                </tr>
              </thead>

              <tbody>
                {historial.isLoading
                  ? Array.from({ length: 5 }).map((_, fila) => (
                      <tr key={fila} className="border-t border-ink-100">
                        {Array.from({ length: 12 }).map((__, columna) => (
                          <td key={columna} className="px-2 py-3">
                            <Skeleton className="h-4 w-full" />
                          </td>
                        ))}
                      </tr>
                    ))
                  : movimientos.map((fila) => {
                      const tachado = fila.anulado || fila.tipo_tono === 'neutral';

                      return (
                        <tr
                          key={fila.id}
                          className={cn('border-t border-ink-100', fila.anulado && 'text-ink-500')}
                        >
                          <td className="px-2 py-2 text-ink-500 tabular-nums">{fila.id}</td>

                          <td className="px-2 py-2 whitespace-nowrap">
                            {formatDateValue(fila.fecha)}
                          </td>

                          <td className="px-2 py-2">
                            <Badge variant={fila.tipo_tono}>{fila.tipo_etiqueta}</Badge>
                            {fila.anulado ? (
                              <p className="mt-1 text-[11px] text-ink-500">
                                Anulado por {fila.anulado_por ?? 'el sistema'}
                              </p>
                            ) : null}
                          </td>

                          <td className="px-2 py-2 text-ink-600">
                            {fila.categoria_etiqueta ?? '—'}
                          </td>

                          <td className="max-w-[18rem] px-2 py-2">
                            <span className="block truncate" title={fila.concepto ?? ''}>
                              {fila.concepto || '—'}
                            </span>
                            {fila.anula_a ? (
                              <span className="text-[11px] text-ink-500">
                                Revierte el movimiento #{fila.anula_a}
                              </span>
                            ) : null}
                          </td>

                          <td className="max-w-[12rem] px-2 py-2">
                            <span className="block truncate" title={fila.referencia ?? ''}>
                              {fila.referencia || '—'}
                            </span>
                          </td>

                          <td
                            className={cn(
                              'px-2 py-2 text-right font-semibold whitespace-nowrap tabular-nums',
                              tachado ? 'text-ink-400 line-through' : 'text-emerald-700',
                            )}
                          >
                            {fila.ingreso === null ? '—' : formatCurrency(fila.ingreso)}
                          </td>

                          <td
                            className={cn(
                              'px-2 py-2 text-right font-semibold whitespace-nowrap tabular-nums',
                              tachado ? 'text-ink-400 line-through' : 'text-rose-600',
                            )}
                          >
                            {fila.egreso === null ? '—' : formatCurrency(fila.egreso)}
                          </td>

                          <td className="px-2 py-2 text-right font-semibold whitespace-nowrap text-ink-800 tabular-nums">
                            {formatCurrency(fila.saldo_despues)}
                          </td>

                          <td className="px-2 py-2 whitespace-nowrap">
                            <p className="text-ink-700">{fila.usuario}</p>
                            <p className="text-[11px] text-ink-500">{fila.registrado ?? '—'}</p>
                          </td>

                          <td className="px-2 py-2 text-center">
                            {fila.adjunto ? (
                              <Button
                                size="sm"
                                variant="ghost"
                                title={`Ver ${fila.adjunto.archivo}`}
                                aria-label={`Ver el comprobante del movimiento ${fila.id}`}
                                onClick={() => void abrirAdjunto(fila.adjunto!)}
                              >
                                <Paperclip className="size-4 text-brand-600" aria-hidden="true" />
                              </Button>
                            ) : (
                              <span className="text-ink-300">—</span>
                            )}
                          </td>

                          <td className="px-2 py-2 text-right">
                            {fila.puede_revertir ? (
                              <Button
                                size="sm"
                                variant="ghost"
                                title="Revertir el movimiento"
                                aria-label={`Revertir el movimiento ${fila.id}`}
                                onClick={() => {
                                  setError(null);
                                  setPorRevertir(fila);
                                }}
                              >
                                <Ban className="size-4 text-amber-600" aria-hidden="true" />
                              </Button>
                            ) : (
                              <span className="text-ink-300">—</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
              </tbody>

              <tfoot className="bg-ink-50/60 font-semibold text-ink-800">
                <tr className="border-t border-ink-100">
                  <td className="px-2 py-2 text-right" colSpan={6}>
                    Totales del filtro
                  </td>
                  <td className="px-2 py-2 text-right whitespace-nowrap text-emerald-700 tabular-nums">
                    {formatCurrency(totales?.ingresos ?? 0)}
                  </td>
                  <td className="px-2 py-2 text-right whitespace-nowrap text-rose-600 tabular-nums">
                    {formatCurrency(totales?.egresos ?? 0)}
                  </td>
                  <td className="px-2 py-2 text-right whitespace-nowrap tabular-nums">
                    {formatCurrency(totales?.saldo ?? 0)}
                  </td>
                  <td className="px-2 py-2" colSpan={3} />
                </tr>
              </tfoot>
            </table>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3">
            <span className="text-xs text-ink-500">
              Página {pagina} de {paginas.toLocaleString('es-PE')}
              {total > 0
                ? ` · mostrando ${((pagina - 1) * limite + 1).toLocaleString('es-PE')}–${Math.min(
                    pagina * limite,
                    total,
                  ).toLocaleString('es-PE')}`
                : ''}
            </span>

            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={pagina <= 1}
                onClick={() => setPagina((actual) => Math.max(1, actual - 1))}
              >
                Anterior
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={pagina >= paginas}
                onClick={() => setPagina((actual) => Math.min(paginas, actual + 1))}
              >
                Siguiente
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Formularios */}
      <DialogoMovimientoCaja
        abierto={dialogo === 'ingreso' || dialogo === 'egreso'}
        tipo={dialogo === 'egreso' ? 'egreso' : 'ingreso'}
        hoy={hoy}
        saldo={saldo}
        categorias={
          dialogo === 'egreso'
            ? resumen.data?.categorias_egreso ?? []
            : resumen.data?.categorias_ingreso ?? []
        }
        guardando={accionando}
        error={error}
        onCerrar={() => setDialogo(null)}
        onGuardar={guardarMovimiento}
      />

      <DialogoAperturaCaja
        abierto={dialogo === 'apertura'}
        hoy={hoy}
        guardando={accionando}
        error={error}
        onCerrar={() => setDialogo(null)}
        onGuardar={(datos) => {
          setError(null);
          aperturar.mutate(
            { ...datos, clave_idempotencia: claveDe('apertura') },
            {
              onSuccess: terminado('Saldo inicial registrado correctamente.'),
              onError: fallo('No se pudo registrar el saldo inicial.'),
            },
          );
        }}
      />

      <DialogoReajusteCaja
        abierto={dialogo === 'reajuste'}
        hoy={hoy}
        saldo={saldo}
        guardando={accionando}
        error={error}
        onCerrar={() => setDialogo(null)}
        onGuardar={(datos) => {
          setError(null);
          ajustar.mutate(
            { ...datos, clave_idempotencia: claveDe('reajuste') },
            {
              onSuccess: terminado('Reajuste registrado correctamente.'),
              onError: fallo('No se pudo registrar el reajuste.'),
            },
          );
        }}
      />

      <DialogoDepositoCaja
        abierto={dialogo === 'deposito'}
        hoy={hoy}
        saldo={saldo}
        bancos={resumen.data?.bancos ?? []}
        cuentas={resumen.data?.cuentas ?? []}
        guardando={accionando}
        error={error}
        onCerrar={() => setDialogo(null)}
        onGuardar={(datos) => {
          setError(null);
          depositar.mutate(
            { ...datos, clave_idempotencia: claveDe('deposito') },
            {
              onSuccess: terminado(
                'Depósito registrado correctamente. El efectivo se descontó de Caja Chica ' +
                  'y se acreditó en la cuenta bancaria.',
              ),
              onError: fallo('No se pudo registrar el depósito.'),
            },
          );
        }}
      />

      <ConfirmDialog
        open={Boolean(porRevertir)}
        title={`¿Revertir el movimiento #${porRevertir?.id ?? ''}?`}
        description={
          porRevertir?.tipo === 'egreso'
            ? 'Se creará un ingreso por el mismo monto y el movimiento quedará marcado como ' +
              'anulado. Si era un depósito a banco, también se anula su movimiento bancario.'
            : 'Se creará un egreso por el mismo monto y el movimiento quedará marcado como anulado.'
        }
        confirmLabel="Revertir"
        loading={revertir.isPending}
        onConfirm={confirmarReversion}
        onClose={() => setPorRevertir(null)}
      />
    </div>
  );
}
